import React from 'react';
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  alpha,
  useTheme,
} from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import useSearchItems from '../../hooks/items/useSearchItems';
import { SelectedAppointment } from '../../types/appointments';
import useItemQuery from '../../hooks/checkouts/useItemQuery';
import useUser from '../../hooks/global/useUser';
import CheckoutItemsSearchRow from './CheckoutItemsSearchRow';
import { ItemWithCommissions } from '../../types/items';
import useAddOrderItem from '../../hooks/checkouts/useAddOrderItem';
import useAddCustomLine from '../../hooks/checkouts/useAddCustomLine';

const LIST_MAX_HEIGHT = 320;

interface Props {
  appointment?: SelectedAppointment | null;
  onItemAdded?: () => void;
  autoFocus?: boolean;
  includeGiftCards?: boolean;
  giftCardsOnly?: boolean;
  hideSearch?: boolean;
  placeholder?: string;
}

/**
 * In-flow items search for modals: always-visible list below a search input.
 * Items load on mount (no focus required).
 */
function CheckoutItemsSearch({
  appointment,
  onItemAdded,
  autoFocus,
  includeGiftCards = true,
  giftCardsOnly = false,
  hideSearch = false,
  placeholder = 'Search items or enter an amount',
}: Props) {
  const theme = useTheme();
  const { user } = useUser();
  const [sharedQuery, setQuery] = useItemQuery();
  const query = hideSearch ? '' : sharedQuery;
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!autoFocus) return;
    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    // eslint-disable-next-line consistent-return
    return () => cancelAnimationFrame(raf);
  }, [autoFocus]);
  const addOrderItem = useAddOrderItem();
  const addCustomLine = useAddCustomLine();

  const allowItemLibrary =
    !appointment ||
    (!!user?.business?.id &&
      !!appointment?.businessId &&
      user?.business?.id === appointment?.businessId);

  const {
    items: searchItems,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isDebouncing,
  } = useSearchItems(query, {
    enabled: allowItemLibrary,
    includeGiftCards,
    giftCardsOnly,
  });
  const items = allowItemLibrary ? searchItems : [];

  const onAddItem = (item: ItemWithCommissions | null) => {
    if (item) {
      addOrderItem({
        itemId: item.id,
        name: item.name,
        price: item.price,
        category: item.category,
      });
    } else {
      addCustomLine(query);
    }

    onItemAdded?.();
    inputRef.current?.blur();
    setQuery('');
    setFocusedIndex(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (giftCardsOnly) {
        if (focusedIndex >= 0) onAddItem(items[focusedIndex] || null);
      } else {
        onAddItem(focusedIndex >= 0 ? items[focusedIndex - 1] || null : null);
      }
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, items.length));
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
    }
  };

  // null at index 0 represents the "custom item / custom amount" row
  const listData: (ItemWithCommissions | null)[] = giftCardsOnly
    ? [...items]
    : [null, ...items];

  const itemContent = (index: number, item: ItemWithCommissions | null) => (
    <CheckoutItemsSearchRow
      item={item}
      index={index}
      totalCount={listData.length}
      isFocused={focusedIndex === index}
      onAddItem={onAddItem}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {!hideSearch && (
        <TextField
          inputRef={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          fullWidth
          size="small"
          onFocus={() => setFocusedIndex(-1)}
          onKeyDown={onKeyDown}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setQuery(e.target.value)
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{
                      fontSize: '1.125rem',
                      color: theme.palette.text.disabled,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment:
                isFetching || isDebouncing ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} color="primary" />
                  </InputAdornment>
                ) : null,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: 'body2.fontSize',
            },
          }}
        />
      )}

      {/* Always-visible items list */}
      <Box
        sx={{
          height: LIST_MAX_HEIGHT,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          p: 0.75,
          bgcolor: theme.palette.background.paper,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.grey[500], 0.25),
            borderRadius: 2,
          },
        }}>
        <Virtuoso
          data={listData}
          endReached={() => {
            if (isFetching || !hasNextPage) return;
            fetchNextPage();
          }}
          itemContent={itemContent}
          style={{ width: '100%', height: '100%' }}
        />
        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={16} color="primary" />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CheckoutItemsSearch;
